import React, { useEffect, useMemo, useState, useCallback } from 'react';

import {
  IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonSpinner, IonText, IonCol,
  IonCardSubtitle, useIonRouter, useIonAlert, IonIcon,
  IonRouterLink
} from '@ionic/react';
import { useAppContext } from '../AppProvider';
import { checkmarkCircleOutline } from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';

import { getBillingAdapter } from '../components/getBillingAdapter';
import type { DisplayPackage } from './BillingAdapter';

const billing = getBillingAdapter();

interface PlanCardsProps {
    rcMgtURL: any;
    setRcMgtURL: any;
}

type Plan = {
  memberLevelID: string;
  memberLevelStatus: string;
  memberLevelName: string;
  memberLevelDesc: string;
  memberLevelPrice: string;
  memberLevelTier: number; 
  memberLevelRCEntitlement: string;  // used to detect current plan
  memberLevelRCPackage: string;      // joins to RC package.identifier
  memberLevelTimersText: string;
  memberLevelTimersInt: number;
  memberLevelContacts: number;
  memberLevelPlaces: number;
  memberLevelRoutes: number;
  memberLevelKids: number;
  memberLevelPets: number;
  memberLevelVehicles: number;
  memberLevelPartners: number;
};

const PlanCards: React.FC<PlanCardsProps> = ({rcMgtURL, setRcMgtURL}) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<any>([]);
  const [packages, setPackages] = useState<DisplayPackage[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [currentPlanKey, setCurrentPlanKey] = useState<string>('1');
  //const [rcMgtURL, setRcMgtURL] = useState<string | null>(null);

  const { authorizedMember, getAuthorizedMember, levels, revenueCatReady } = useAppContext(); 
  const platform = Capacitor.getPlatform();
  const router = useIonRouter();
  const [showAlert, dismissAlert] = useIonAlert();

  useEffect(() => {
    let mounted = true;
    (async () => {
        if (
            !revenueCatReady ||
            authorizedMember.memberToken === undefined ||
            levels === undefined
            ) return;
 
        try {
            
            // 1) Fetch the plan(level) catalog from backend
            const planCatalog: Plan[] = levels;       
            setCatalog(planCatalog);

            // 2) Fetch RC offering
            const { availablePackages: avail } = await billing.getOfferings();

            // 3) Join: keep only packages that exist in your catalog
            const planByPkg = new Map(planCatalog.map(p => [p.memberLevelRCPackage, p]));
            const joinedPkgs = avail.filter(p => planByPkg.has(p.identifier));

            // 4) Detect current plan via active entitlements (pick the highest level whose primary entitlement is active)
            const info = await billing.getCustomerInfo();
            //console.log(info);
            setRcMgtURL(info.managementURL ?? null);
            const active = info.entitlements.active;
            const activePlans = planCatalog.filter(p => !!active[p.memberLevelRCEntitlement]);
            const current = activePlans.sort((a, b) => b.memberLevelTier - a.memberLevelTier)[0] ?? null;
            
            if (mounted) {
                const orderedPkgs = joinedPkgs.sort((a, b) =>
                    planByPkg.get(a.identifier)!.memberLevelTier - planByPkg.get(b.identifier)!.memberLevelTier
                );

                setPackages(orderedPkgs);
                setPlans(orderedPkgs.map(pkg => planByPkg.get(pkg.identifier)!));
                setCurrentPlanKey(current?.memberLevelID ?? '1');
            }
        } catch (e: any) {
            if (mounted) setErr(e?.message ?? 'Failed to load paywall');
        } finally {
            if (mounted) setLoading(false);
        }
      
    })();
    return () => { mounted = false; };
  }, [revenueCatReady, authorizedMember.memberToken, levels]);

  
  const currentPlanAlert = () => {
      showAlert({
          header: 'Your Current Plan',
          message: 'You are trying to select a plan you already have.',
          buttons: [
              { 
                text: 'Cancel', 
                role: 'cancel',
                cssClass: 'alert-button-cancel'
              }
          ]
      })        
  };

  
  const confirmFreePlan = (plan: object, planID: string, planName: string) => {
      showAlert({
          header: 'Please Confirm:',
          message: 'Are you sure you want to switch to the SoloWise Free plan? We will first cancel your current plan and then start your new plan.',
          buttons: [
              { 
                text: 'Cancel', 
                role: 'cancel',
                cssClass: 'alert-button-cancel'
              },
              {
                  text: 'Switch Plans',
                  role: 'confirm',
                  cssClass: 'alert-button-confirm',
                  handler: () => handleFreePlan(plan, planID, planName),
              }
          ]
      })        
  };


  const confirmSwitchPlan = (rcPackage: DisplayPackage, planID: string, planName: string) => {
      showAlert({
          header: 'Please Confirm:',
          message: 'Are you sure you want to switch to the SoloWise '+ planName +' plan? We will first cancel your current plan and then start your new plan.',
          buttons: [
              { 
                text: 'Cancel', 
                role: 'cancel',
                cssClass: 'alert-button-cancel'
              },
              {
                  text: 'Switch Plans',
                  role: 'confirm',
                  cssClass: 'alert-button-confirm',
                  handler: () => handleSwitchPlan(rcPackage, planID, planName),
              }
          ]
      })        
  };


  const handleSwitchPlan = useCallback(
    async (pkg: DisplayPackage, planID: string, planName: string) => {
        await dismissAlert();
        setBusyId(pkg.identifier);
        
        try {
            const info: any = await billing.getCustomerInfo();
            
            const PAID_ENTITLEMENTS = ['solowise_plus', 'solowise_pro', 'solowise_platinum'] as const;
            const hasPaid = PAID_ENTITLEMENTS.some(e => !!info.entitlements.active[e]);
            /*const hasPaid =
                !!info.entitlements.active['solowise_plus'] ||
                !!info.entitlements.active['solowise_pro'] ||
                !!info.entitlements.active['solowise_platinum'];*/

            if (hasPaid && platform === 'web') {
                const formData = new FormData(); 
                formData.append('mt', authorizedMember.memberToken);
                formData.append('levelID', planID);
                formData.append('levelName', planName);
                const resp = await fetch('https://api.besolowise.com/members/billing/cancel-sub/', {
                method: 'POST',
                credentials: 'include',
                body: formData,
                });
                const json = await resp.json();

                //console.log(json);
                //console.log(json.resp);

                if (!resp.ok || !json.ok) throw new Error(json.error || 'Cancellation failed');

                // Tiny delay to avoid any race (optional)
                await new Promise(r => setTimeout(r, 400));

                await getAuthorizedMember(authorizedMember.memberToken);
            }

            if (planID == '1') return;

            // Now start the new purchase
            const customerInfo: any = await billing.purchasePackage(pkg, {
                customerEmail: authorizedMember.memberEmail,
            });

            const active = customerInfo.entitlements.active;
            console.log('Active entitlements after switch:', Object.keys(active));
            
            //Refresh UI (current plan)
            await getAuthorizedMember(authorizedMember.memberToken);
            //router.push('/account/dashboard', 'forward');  
            
        } catch (e) {
            console.warn('Switch flow ended', e);
        } finally {
            setBusyId(null);
        }
  },[authorizedMember?.memberEmail, authorizedMember?.memberToken]);


  const handleFreePlan = useCallback(
    async (plan: object, planID: string, planName: string) => {
        await dismissAlert();
        //setBusyId(plan.planID);
        try {
            const info: any = await billing.getCustomerInfo();
            const hasPaid =
                !!info.entitlements.active['solowise_plus'] ||
                !!info.entitlements.active['solowise_pro'] ||
                !!info.entitlements.active['solowise_platinum'];

            if (hasPaid) {
                const formData = new FormData(); 
                formData.append('mt', authorizedMember.memberToken);
                formData.append('levelID', planID);
                formData.append('levelName', planName);
                const resp = await fetch('https://api.besolowise.com/members/billing/cancel-sub/', {
                method: 'POST',
                credentials: 'include',
                body: formData,
                });
                const json = await resp.json();

                //console.log(json);
                //console.log(json.resp);

                if (!resp.ok || !json.ok) throw new Error(json.error || 'Cancellation failed');

                // Tiny delay to avoid any race (optional)
                await new Promise(r => setTimeout(r, 400));

                await getAuthorizedMember(authorizedMember.memberToken);
            }

            //Refresh UI (current plan)
            await getAuthorizedMember(authorizedMember.memberToken);
            //router.push('/account/dashboard', 'forward');  
            
        } catch (e) {
            console.warn('Switch flow ended', e);
        } finally {
            setBusyId(null);
        }
  },[authorizedMember?.memberEmail]);


  const cards = useMemo(() => {
    return packages.map((pkg, i) => {
      const plan = plans[i]; // same order/index
      const isCurrent = plan?.memberLevelID === currentPlanKey;

      const cta = isCurrent
        ? 'Current plan'
        : (plan.memberLevelTier > (plans.find(p => p.memberLevelID === currentPlanKey)?.memberLevelTier ?? 0))
          ? `Upgrade to ${plan.memberLevelName}`
          : `Switch to ${plan.memberLevelName}`;

      const locked = busyId === pkg.identifier || isCurrent;

      return (
        <IonCol key={i} size='12' sizeMd='6' sizeLg='6' sizeXl='6' className="equal-height-col">
            <IonCard className='equal-height-card' key={pkg.identifier}>
                <IonCardHeader className='ion-padding-bottom'>
                    <IonCardTitle color={'dark'}>SoloWise {plan.memberLevelName}</IonCardTitle>
                    <IonCardSubtitle className='ion-text-end' color={'tertiary'}><strong>{pkg.priceText} / mo.</strong></IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent className='equal-height-card-content ion-padding-top'>
                    <p>{plan.memberLevelDesc}</p>
                    <ul>
                        <li>Emergency Contacts: {plan.memberLevelContacts}</li>
                        {plan.memberLevelKids > 0 && (<li>Kids: {plan.memberLevelKids}</li>)}
                        {plan.memberLevelPets > 0 && (<li>Pets: {plan.memberLevelPets}</li>)}
                        {plan.memberLevelVehicles > 0 && (<li>Vehicles: {plan.memberLevelVehicles}</li>)}
                        <li>Places: {plan.memberLevelPlaces}</li>
                        <li>Routes: {plan.memberLevelRoutes}</li>
                        {plan.memberLevelPartners > 0 && (<><li>Partners: {plan.memberLevelPartners == 999 ? 'Unlimited' : plan.memberLevelPartners}</li></>)}
                        <li><strong>Timer Alarms: {plan.memberLevelTimersText} <IonText color={'warning'}>*</IonText></strong></li>
                    </ul>
                    
                    <div className="equal-height-card-footer">  
                        <IonButton className='ion-margin-top' color={isCurrent ? 'success' : 'secondary'} onClick={isCurrent ? currentPlanAlert : () => confirmSwitchPlan(pkg, plan.memberLevelID, plan.memberLevelName)} disabled={locked} expand="block" fill="solid">
                            {busyId === pkg.identifier ? 'Processing…' : (isCurrent ? <><IonIcon className='ion-margin-end' icon={checkmarkCircleOutline}></IonIcon> Your Current Plan</> : cta)}
                        </IonButton>
                    </div>
                </IonCardContent>
            </IonCard>
        </IonCol>
      );
    });
  }, [packages, plans, currentPlanKey, busyId, handleSwitchPlan]);

  if (loading) return <div className='ion-text-center ion-padding'><IonSpinner /></div>;
  if (err) return <IonText color="danger">{err}</IonText>;
  if (!packages.length) return <IonText>No purchase options are currently available.</IonText>;

  return (
    <>    
        <IonCol size='12' sizeMd='6' sizeLg='6' sizeXl='6' className="equal-height-col">
            <IonCard className='equal-height-card' key={0}>
                <IonCardHeader className='ion-padding-bottom'>
                    <IonCardTitle color={'dark'}>SoloWise {catalog[0].memberLevelName}</IonCardTitle>
                    <IonCardSubtitle className='ion-text-end' color={'tertiary'}><strong>ALWAYS FREE</strong></IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent className='equal-height-card-content ion-padding-top'>
                    <p>{catalog[0].memberLevelDesc}</p>
                    <ul>
                        <li>Emergency Contacts: {catalog[0].memberLevelContacts}</li>
                        {catalog[0].memberLevelKids > 0 && (<li>Kids: {catalog[0].memberLevelKids}</li>)}
                        {catalog[0].memberLevelPets > 0 && (<li>Pets: {catalog[0].memberLevelPets}</li>)}
                        {catalog[0].memberLevelVehicles > 0 && (<li>Vehicles: {catalog[0].memberLevelVehicles}</li>)}
                        <li>Places: {catalog[0].memberLevelPlaces}</li>
                        <li>Routes: {catalog[0].memberLevelRoutes}</li>
                        {catalog[0].memberLevelPartners > 0 && (<><li>Partners: {catalog[0].memberLevelPartners == 999 ? 'Unlimited' : catalog[0].memberLevelPartners}</li></>)}
                        <li><strong>Timer Alarms: {catalog[0].memberLevelTimersText} <IonText color={'warning'}>*</IonText></strong></li>
                    </ul>
                    
                    <div className="equal-height-card-footer">
                        <IonButton color={currentPlanKey === '1' ? 'success' : 'secondary'} disabled={currentPlanKey === '1'} expand="block" onClick={currentPlanKey === '1' ? currentPlanAlert : () => confirmFreePlan(catalog[0], catalog[0].memberLevelID, catalog[0].memberLevelName)}>{currentPlanKey === '1' ? <><IonIcon className='ion-margin-end' icon={checkmarkCircleOutline}></IonIcon> Your Current Plan</> : 'Switch to Free'}</IonButton>
                    </div>
                </IonCardContent>
            </IonCard>
        </IonCol>
        {cards}
    </>
  );
}

export default PlanCards;